import * as _ from "lodash"
import { getRandom } from "services/cachedRandom/random.service"

/**
 * Replaces dynamicMaf in plans with absolute values
 * Takes in current price and plans
 * Returns new plans
 */
export const replaceDynamicPrices = (currentPrice, plans) => {
  // basic checks to make sure we are sending the variables into this
  if (currentPrice === null) {
    throw new Error("No current price provided")
  }

  if (plans === null || plans.length === 0) {
    throw new Error("No plans provided")
  }

  // create a clone so we're not mutating the actual plans
  const mutablePlans = _.cloneDeep(plans)

  // the main bulk of the logic
  // go through each plan, check through the devices and replace relativeMaf as needed
  // then delete relativeMaf so we can't accidentally recalculate later
  const replacedPlans = mutablePlans.reduce((outputPlans, plan) => {
    // Go through the devices and convert the relativeMaf
    const alteredDevices = Object.keys(plan.devices).reduce(
      (devices, deviceKey) => {
        const device = plan.devices[deviceKey]

        let basePrice = currentPrice

        if ("relativeToPlan" in device) {
          const basePlan = outputPlans.find(p => p.id === device.relativeToPlan)
          if (basePlan === undefined) {
            throw new Error(`Plan ID ${device.relativeToPlan} not found`)
          }

          if (!(deviceKey in basePlan.devices)) {
            throw new Error(
              `Plan ID ${device.relativeToPlan} doesn't have device ${deviceKey}`
            )
          }
          basePrice = basePlan.devices[deviceKey].maf
        }

        if ("relativeMaf" in device) {
          // relative, so replace maf
          device.maf = basePrice + device.relativeMaf
          delete device.relativeMaf
        }

        return { ...devices, [deviceKey]: device }
      },
      {}
    )

    // override the devices in the plan
    plan.devices = alteredDevices

    // push it to the output
    outputPlans.push(plan)

    return outputPlans
  }, [])

  return replacedPlans
}

/**
 * Checks to see if there's mafArray or relativeMafArray properties
 * If so, this picks a cached random element and override the original property
 */
export const replaceArrayPrices = (plans, options = {}) => {
  if (plans === null || plans.length === 0) {
    throw new Error("No plans provided")
  }

  const mutablePlans = _.cloneDeep(plans)

  return mutablePlans.map(plan => {
    const alteredDevices = Object.keys(plan.devices).reduce(
      (devices, deviceKey) => {
        const device = plan.devices[deviceKey]

        if ("mafArray" in device) {
          const randomID = options.mafArrayCostsAreSame
            ? `mafArrayIndex-shared`
            : `mafArrayIndex-${plan.id}`
          device.maf = getRandom(randomID, device.mafArray)
        }

        if ("upfrontArray" in device) {
          const randomID = options.upfrontCostsAreSame
            ? `upfrontArrayIndex-shared`
            : `upfrontArrayIndex-${plan.id}`
          device.upfront = getRandom(randomID, device.upfrontArray)
        }

        if ("relativeMafArray" in device) {
          const randomID = options.upfrontCostsAreSame
            ? `relMafArrayIndex-shared`
            : `relMafArrayIndex-${plan.id}`
          device.relativeMaf = getRandom(randomID, device.relativeMafArray)
        }

        devices[deviceKey] = device
        return devices
      },
      {}
    )

    plan.devices = alteredDevices

    return plan
  })
}

// HACK: This should be in its own file
// Ideally this should be very generic
export const replaceDataArrays = plans => {
  const mutablePlans = _.cloneDeep(plans)

  return mutablePlans.map(plan => {
    // nameArray
    if ("nameArray" in plan.data) {
      plan.data.name = getRandom(
        `nameDLArrayIndex-${plan.id}`,
        plan.data.nameArray
      )
      delete plan.data.nameArray
    }

    // downloadSpeedArray
    if ("downloadSpeedArray" in plan.data) {
      plan.data.downloadSpeed = getRandom(
        `nameDLArrayIndex-${plan.id}`,
        plan.data.downloadSpeedArray
      )
      delete plan.data.downloadSpeedArray
    }

    return plan
  })
}
